import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Paper, Box, Grid, Tabs, Tab, TextField,
    Card, CardContent, CardActions, Button, Chip, IconButton, Tooltip,
    CircularProgress, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
    ToggleButtonGroup, ToggleButton, Pagination, Snackbar, Alert
} from '@mui/material';
import {
    Search as SearchIcon,
    Favorite as FavoriteIcon,
    FavoriteBorder as FavoriteBorderIcon,
    Delete as DeleteIcon,
    Archive as ArchiveIcon,
    Style as FlashcardIcon,
    ViewList as ListIcon,
    Quiz as QuizIcon,
    VolumeUp as VolumeUpIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckIcon,
    Cancel as WrongIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import api from '../services/api';

const CONFIDENCE_COLORS = ['#f44336', '#ff9800', '#ffc107', '#4caf50'];
const CONFIDENCE_LABELS = ['New', 'Learning', 'Familiar', 'Mastered'];

const Vocabulary = () => {
    const [tab, setTab] = useState(0); // 0=word bank, 1=flashcards
    const [words, setWords] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('recent');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Flashcard state
    const [flashcards, setFlashcards] = useState([]);
    const [currentCard, setCurrentCard] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [flashcardLoading, setFlashcardLoading] = useState(false);
    const [sessionComplete, setSessionComplete] = useState(false);
    const [sessionStats, setSessionStats] = useState({ correct: 0, wrong: 0 });

    const fetchWords = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/vocabulary', {
                params: { search, sort, page, limit: 20 }
            });
            setWords(res.data.words || []);
            setTotalPages(res.data.totalPages || 1);
        } catch (err) {
            console.error('Error fetching vocabulary:', err);
        } finally {
            setLoading(false);
        }
    }, [search, sort, page]);

    const fetchStats = async () => {
        try {
            const res = await api.get('/vocabulary/stats');
            setStats(res.data);
        } catch (err) {
            console.error('Error fetching vocab stats:', err);
        }
    };

    useEffect(() => {
        fetchWords();
        fetchStats();
    }, [fetchWords]);

    const handleFavorite = async (id) => {
        try {
            await api.patch(`/vocabulary/${id}/favorite`);
            setWords(prev => prev.map(w => w._id === id ? { ...w, favorite: !w.favorite } : w));
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
        }
    };

    const handleArchive = async (id) => {
        try {
            await api.patch(`/vocabulary/${id}/archive`);
            setWords(prev => prev.filter(w => w._id !== id));
            setSnackbar({ open: true, message: 'Word archived', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to archive', severity: 'error' });
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/vocabulary/${id}`);
            setWords(prev => prev.filter(w => w._id !== id));
            setSnackbar({ open: true, message: 'Word deleted', severity: 'success' });
            fetchStats();
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
        }
    };

    // Flashcard functions
    const startFlashcards = async () => {
        try {
            setFlashcardLoading(true);
            setSessionComplete(false);
            setSessionStats({ correct: 0, wrong: 0 });
            setCurrentCard(0);
            setFlipped(false);
            const res = await api.get('/vocabulary/flashcards');
            setFlashcards(res.data.cards || []);
            if ((res.data.cards || []).length === 0) {
                setSnackbar({ open: true, message: 'No cards due for review! Come back later.', severity: 'info' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to load flashcards', severity: 'error' });
        } finally {
            setFlashcardLoading(false);
        }
    };

    const handleFlashcardAnswer = async (correct) => {
        const card = flashcards[currentCard];
        if (!card) return;

        try {
            await api.post('/vocabulary/flashcards/answer', {
                wordId: card._id,
                correct
            });
        } catch (err) {
            // Non-critical
        }

        setSessionStats(prev => ({
            correct: prev.correct + (correct ? 1 : 0),
            wrong: prev.wrong + (correct ? 0 : 1)
        }));

        if (currentCard + 1 >= flashcards.length) {
            setSessionComplete(true);
        } else {
            setCurrentCard(prev => prev + 1);
            setFlipped(false);
        }
    };

    // Word Bank Tab
    const WordBank = () => (
        <Box>
            {/* Stats Cards */}
            {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="primary">{stats.totalWords}</Typography>
                            <Typography variant="caption" color="text.secondary">Total Words</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="success.main">{stats.masteredWords}</Typography>
                            <Typography variant="caption" color="text.secondary">Mastered</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.favorites || 0}</Typography>
                            <Typography variant="caption" color="text.secondary">Favorites</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="info.main">{stats.languagesCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Languages</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Search + Sort Controls */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search words..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    sx={{ flexGrow: 1, minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select value={sort} label="Sort by" onChange={(e) => { setSort(e.target.value); setPage(1); }}>
                        <MenuItem value="recent">Most Recent</MenuItem>
                        <MenuItem value="alphabetical">Alphabetical</MenuItem>
                        <MenuItem value="most-looked-up">Most Looked Up</MenuItem>
                        <MenuItem value="least-confident">Least Confident</MenuItem>
                        <MenuItem value="due-review">Due for Review</MenuItem>
                    </Select>
                </FormControl>
            </Paper>

            {/* Word Cards */}
            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
            ) : words.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">No words yet</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Words you look up while reading will appear here.
                    </Typography>
                </Paper>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {words.map((word) => (
                            <Grid item xs={12} sm={6} md={4} key={word._id}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="h6" fontWeight="bold">{word.word}</Typography>
                                            <Chip 
                                                label={CONFIDENCE_LABELS[word.flashcard?.confidence || 0]}
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: CONFIDENCE_COLORS[word.flashcard?.confidence || 0] + '20',
                                                    color: CONFIDENCE_COLORS[word.flashcard?.confidence || 0],
                                                    fontWeight: 600
                                                }}
                                            />
                                        </Box>
                                        {word.phonetic && (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                                                /{word.phonetic}/
                                            </Typography>
                                        )}
                                        {word.romanization && (
                                            <Typography variant="body2" color="info.main" sx={{ mb: 0.5 }}>
                                                {word.romanization}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" sx={{ mt: 1 }}>{word.definition}</Typography>
                                        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {word.language && (
                                                <Chip label={word.language.toUpperCase()} size="small" variant="outlined" />
                                            )}
                                            {word.source && (
                                                <Chip label={word.source} size="small" variant="outlined" color="primary" />
                                            )}
                                            {word.lookupCount > 1 && (
                                                <Chip label={`${word.lookupCount}x`} size="small" variant="outlined" color="warning" />
                                            )}
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                                        <Tooltip title={word.favorite ? 'Unfavorite' : 'Favorite'}>
                                            <IconButton size="small" onClick={() => handleFavorite(word._id)}>
                                                {word.favorite ? <FavoriteIcon color="error" fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Archive">
                                            <IconButton size="small" onClick={() => handleArchive(word._id)}>
                                                <ArchiveIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => handleDelete(word._id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );

    // Flashcard Tab
    const FlashcardGame = () => (
        <Box>
            {flashcards.length === 0 && !flashcardLoading && !sessionComplete && (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <FlashcardIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>Flashcard Review</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Test your memory with spaced repetition. Words that are harder for you will appear more often.
                    </Typography>
                    <Button variant="contained" size="large" onClick={startFlashcards} startIcon={<QuizIcon />}>
                        Start Session
                    </Button>
                </Paper>
            )}

            {flashcardLoading && (
                <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
            )}

            {flashcards.length > 0 && !sessionComplete && !flashcardLoading && (
                <Box sx={{ maxWidth: 500, mx: 'auto' }}>
                    {/* Progress */}
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Card {currentCard + 1} of {flashcards.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                ✓ {sessionStats.correct} | ✗ {sessionStats.wrong}
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={((currentCard) / flashcards.length) * 100}
                            sx={{ borderRadius: 2, height: 6 }}
                        />
                    </Box>

                    {/* Card */}
                    <Paper 
                        sx={{ 
                            p: 4, 
                            textAlign: 'center', 
                            minHeight: 250, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.01)' }
                        }}
                        onClick={() => setFlipped(!flipped)}
                    >
                        {!flipped ? (
                            <>
                                <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
                                    {flashcards[currentCard]?.word}
                                </Typography>
                                {flashcards[currentCard]?.phonetic && (
                                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        /{flashcards[currentCard].phonetic}/
                                    </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                                    Tap to reveal definition
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                    {flashcards[currentCard]?.word}
                                </Typography>
                                <Typography variant="h5" sx={{ mb: 2 }}>
                                    {flashcards[currentCard]?.definition}
                                </Typography>
                                {flashcards[currentCard]?.romanization && (
                                    <Typography variant="body2" color="info.main">
                                        {flashcards[currentCard].romanization}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Paper>

                    {/* Answer buttons */}
                    {flipped && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="large"
                                startIcon={<WrongIcon />}
                                onClick={() => handleFlashcardAnswer(false)}
                                sx={{ flex: 1, py: 1.5 }}
                            >
                                Didn't Know
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                startIcon={<CheckIcon />}
                                onClick={() => handleFlashcardAnswer(true)}
                                sx={{ flex: 1, py: 1.5 }}
                            >
                                Got It
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Session Complete */}
            {sessionComplete && (
                <Paper sx={{ p: 6, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
                    <Typography variant="h4" gutterBottom>Session Complete! 🎉</Typography>
                    <Box sx={{ my: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, backgroundColor: 'success.main', color: 'white' }}>
                                    <Typography variant="h3" fontWeight="bold">{sessionStats.correct}</Typography>
                                    <Typography>Correct</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, backgroundColor: 'error.main', color: 'white' }}>
                                    <Typography variant="h3" fontWeight="bold">{sessionStats.wrong}</Typography>
                                    <Typography>Missed</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                            {sessionStats.correct > sessionStats.wrong 
                                ? 'Great job! Keep it up!' 
                                : 'Keep practicing — you\'ll improve!'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button variant="contained" onClick={startFlashcards} startIcon={<RefreshIcon />}>
                            New Session
                        </Button>
                        <Button variant="outlined" onClick={() => { setFlashcards([]); setTab(0); }}>
                            Back to Words
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">
                    Vocabulary Bank
                </Typography>
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
                <Tab icon={<ListIcon />} label="Word Bank" iconPosition="start" />
                <Tab icon={<FlashcardIcon />} label="Flashcards" iconPosition="start" />
            </Tabs>

            {tab === 0 && <WordBank />}
            {tab === 1 && <FlashcardGame />}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Vocabulary;
